import { Document as DocumentT } from "@prismicio/types-internal/lib/content"
import { type ReactElement, Suspense, useState } from "react"
import { type Location, useLocation } from "react-router-dom"
import { z } from "zod"

import {
  prefetchStaticCustomType,
  useStaticCustomType,
} from "../app/services/customTypes"
import { usePrefetchTeamSpacesQuery } from "../app/services/role"
import { Document } from "./Document"
import { type NewDocumentData, useDocumentMetaTitle } from "./documentData"
import { DocumentProvider } from "./DocumentProvider"
import { useNewDocumentParams } from "./documentRouting"
import { DocumentSkeleton } from "./DocumentSkeleton"
import { UIDProvider } from "./UIDProvider"

export function NewDocument() {
  return (
    <Suspense fallback={<DocumentSkeleton screen="new" />}>
      <UIDProvider>
        <DataLoader>
          {(data) => (
            <DocumentProvider initData={data}>
              <Document />
            </DocumentProvider>
          )}
        </DataLoader>
      </UIDProvider>
    </Suspense>
  )
}

interface DataLoaderProps {
  children: (data: NewDocumentData) => ReactElement
}

function DataLoader(props: DataLoaderProps) {
  const { children } = props

  const { customTypeId, groupLangId, locale, releaseId } =
    useNewDocumentParams()

  // Prefetch data in parallel.
  prefetchStaticCustomType(customTypeId)
  usePrefetchTeamSpacesQuery() // Used to create back button link respecting team spaces

  const customType = useStaticCustomType(customTypeId).data

  const location = useLocation()
  const asCopy = useValidCopyDoc(location)

  const documentData = {
    type: "new",
    groupLangId,
    locale,
    customType,
    asCopy,
    releaseId,
  } as const

  useDocumentMetaTitle(documentData)

  return children(documentData)
}

const locationType = z.object({
  state: z.object({
    document: z.record(z.unknown()),
    title: z.string(),
    tags: z.array(z.string()),
  }),
})

function parseLocationType(location: Location) {
  const parsed = locationType.safeParse(location)
  if (!parsed.success) return
  return parsed.data.state
}

function useValidCopyDoc(location: Location) {
  /**
   * We're caching here the initial location state from useLocation in React state
   * because the state disappears from useLocation after any action
   * that creates a new location entry.
   * For example, when a user selects slices and the slice IDs are updated in the URL,
   * a new location in react-router gets created without any state.
   * And we need the state to determine whether the doc was created as a copy or not,
   * which is why we cache it.
   */
  const [state] = useState(() => parseLocationType(location))

  if (!state) return

  const document = validateDocument(state.document)

  return { ...state, document }
}

function validateDocument(maybeDoc: object) {
  const result = DocumentT.decode(maybeDoc)
  if (result._tag === "Left") {
    console.warn("could not decode document")
    return {}
  }
  return result.right
}
